import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  InvestorRequestBody,
  Country,
  SaveDraftPayload,
  DraftValuesResponse,
  WireInstructionSubmitResponse,
} from "@investorRequest/models";
import {
  validateFields,
  setWireUnavailable,
  resetFormFields,
  setC2DSErrors,
  clearAllC2DSErrors,
} from "@investorRequest/store/ducks";
import { investorRequestSelector } from "@investorRequest/store/selectors";
import { openNotificationThunk } from "@store/Notification/thunks";
import { ThunkApiConfig } from "@shared/models/redux";
import { parseC2DSErrors } from "@investorRequest/utils/c2dsErrorParser";
import FeatureToggleService from "@services/FeatureToggleService";
import { ToggledFeature } from "@shared/models/featureToggle";
import { selectInternalExternalPresence } from "@store/Entities/ducks";

export const sendInvestorRequestThunk = createAsyncThunk<boolean, InvestorRequestBody, ThunkApiConfig>(
  "investorRequest/sendInvestorRequest",
  async (body, { rejectWithValue, dispatch, extra }) => {
    try {
      const response = await extra<boolean, InvestorRequestBody>("/entitlements/v1/user/InvestorRequest", {
        method: "post",
        body,
      });

      if (response.data) {
        dispatch(
          openNotificationThunk({
            variant: "info",
            text: "Your request has been sent.",
          })
        );
      }

      return response.data;
    } catch (error: any) {
      const errorMessage = error.response?.data?.[0] || "Some error occurred. Please try again.";

      dispatch(
        openNotificationThunk({
          variant: "warning",
          text: errorMessage,
        })
      );

      return rejectWithValue(errorMessage);
    }
  }
);

// LPC-9775: Delete draft API
export const deleteDraftThunk = createAsyncThunk<
  boolean,
  { fundType: string; suppressNotification?: boolean },
  ThunkApiConfig
>(
  "investorRequest/deleteDraft",
  async ({ fundType, suppressNotification = false }, { rejectWithValue, dispatch, extra }) => {
    try {
      const response = await extra<boolean>(
        `/entitlements/v1/user/WireInstruction/DeleteDraft?fundType=${encodeURIComponent(fundType)}`,
        {
          method: "delete",
        }
      );

      if (!suppressNotification) {
        dispatch(
          openNotificationThunk({
            variant: "info",
            text: "Draft cleared successfully.",
          })
        );
      }

      return response.data;
    } catch (error: any) {
      // Handle 404 (no draft exists) gracefully
      if (error.response?.status === 404) {
        if (!suppressNotification) {
          dispatch(
            openNotificationThunk({
              variant: "info",
              text: "No draft found to clear.",
            })
          );
        }

        return true; // Return success since clearing a non-existent draft is effectively successful
      }

      const errorMessage = error.response?.data?.[0] || "Failed to clear draft. Please try again.";

      if (!suppressNotification) {
        dispatch(
          openNotificationThunk({
            variant: "warning",
            text: errorMessage,
          })
        );
      }

      return rejectWithValue(errorMessage);
    }
  }
);

export const submitWireInstructionThunk = createAsyncThunk<
  WireInstructionSubmitResponse,
  InvestorRequestBody,
  ThunkApiConfig
>("investorRequest/submitWireInstruction", async (body, { rejectWithValue, dispatch, extra, getState }) => {
  try {
    const response = await extra<any, InvestorRequestBody>("/entitlements/v1/user/WireInstruction/submit", {
      method: "post",
      body,
    });

    // Handle response - it might be a boolean or an object with attachment info
    const responseData = response.data;
    const isSuccess = typeof responseData === "boolean" ? responseData : responseData?.success !== false;

    if (isSuccess) {
      // LPC-10289: Clear any C2DS errors on successful submission
      dispatch(clearAllC2DSErrors());

      const { hasInternal, hasExternal } = selectInternalExternalPresence(getState() as any);
      const isHybrid = hasInternal && hasExternal;
      const state = investorRequestSelector(getState());
      const fundType =
        body.customFields?.fundType ||
        (state.activeInfoTab === "Internal Fund Information"
          ? "Internal"
          : state.activeInfoTab === "External Fund Information"
          ? "External"
          : undefined);

      if (isHybrid && fundType === "Internal") {
        dispatch(
          openNotificationThunk({
            variant: "info",
            text: "Your wire instruction submitted successfully. This update will only impact your INTERNAL fund information. To update your wire instructions for EXTERNAL funds, please select the EXTERNAL Fund Information tab and submit a separate Wire Change Request.",
          })
        );
      } else if (isHybrid && fundType === "External") {
        dispatch(
          openNotificationThunk({
            variant: "info",
            text: "Your wire instruction submitted successfully. This update will only impact your EXTERNAL fund information. To update your wire instructions for INTERNAL funds, please select the INTERNAL Fund Information tab and submit a separate Wire Change Request.",
          })
        );
      } else {
        dispatch(
          openNotificationThunk({
            variant: "info",
            text: "Your wire instruction submitted successfully.",
          })
        );
      }

      // Extract attachment information from response if present
      // Handle the format: attachmentURL, attachmentId, attachmentName
      let attachmentId: string | undefined;
      let attachmentName: string | undefined;
      let attachmentURL: string | undefined;

      if (typeof responseData === "object" && responseData !== null) {
        // Handle different possible response formats (camelCase, PascalCase, snake_case)
        attachmentId =
          responseData.attachmentId ||
          responseData.attachmentID ||
          responseData.attachment_id ||
          responseData.AttachmentId;
        attachmentName = responseData.attachmentName || responseData.attachment_name || responseData.AttachmentName;
        attachmentURL =
          responseData.attachmentURL ||
          responseData.attachmentUrl ||
          responseData.attachment_url ||
          responseData.AttachmentURL;
      }

      // Log attachment info if present (for debugging/monitoring)
      if (attachmentId || attachmentName || attachmentURL) {
        console.log("Attachment information from response:", {
          attachmentId,
          attachmentName,
          attachmentURL,
        });
      }

      // After successful submission, delete the draft if it exists
      if (fundType) {
        // Delete draft silently (don't show notification as submission already succeeded)
        try {
          await dispatch(deleteDraftThunk({ fundType, suppressNotification: true })).unwrap();
        } catch (draftError) {
          console.warn("Failed to delete draft after successful submission:", draftError);
        }
      } else {
        // If fundType is not in customFields, try to calculate it from state
        const state = investorRequestSelector(getState());
        const { activeInfoTab } = state;

        // Try to determine fundType from activeInfoTab
        if (activeInfoTab) {
          const calculatedFundType = activeInfoTab === "Internal Fund Information" ? "Internal" : "External";

          try {
            await dispatch(deleteDraftThunk({ fundType: calculatedFundType, suppressNotification: true })).unwrap();
          } catch (draftError) {
            console.warn("Failed to delete draft after successful submission:", draftError);
          }
        }
      }

      // Reset form fields after successful submission
      // LPC-9775: Pass fundType to clear only that session (preserve other tab's attachment)
      dispatch(resetFormFields({ fundType: fundType || undefined }));

      // Return response with attachment info if present
      return {
        success: true,
        attachmentId,
        attachmentName,
        attachmentURL,
      };
    }

    return {
      success: false,
    };
  } catch (error: any) {
    let errorMessage = error.response?.data || "Please finish filling out information.";

    // Remove trailing semicolon if present
    if (typeof errorMessage === "string" && errorMessage.endsWith(";")) {
      errorMessage = errorMessage.slice(0, -1);
    }

    dispatch(
      openNotificationThunk({
        variant: "warning",
        text: errorMessage,
      })
    );

    // LPC-10289: Parse C2DS errors and map to form fields
    if (typeof error.response?.data === "string") {
      // Get routing number values from the request body to intelligently determine
      // which routing number field is invalid when C2DS returns a single error
      const routingNumber = body.customFields?.routingNumber as string | undefined;

      // Intermediary routing number is nested inside IntermediaryInstructions array
      // (it gets moved there and deleted from root in submitFormThunk)
      const intermediaryInstructions = body.customFields?.IntermediaryInstructions as
        | Array<{ routingNumber?: string }>
        | undefined;
      const intermediaryRoutingNumber = intermediaryInstructions?.[0]?.routingNumber;

      const c2dsParseResult = parseC2DSErrors(error.response.data, {
        routingNumber,
        intermediaryRoutingNumber,
      });

      if (c2dsParseResult.hasErrors) {
        dispatch(setC2DSErrors(c2dsParseResult.fieldErrors));
      }
    }

    return rejectWithValue(errorMessage);
  }
});

export const submitFormThunk = createAsyncThunk<void, void, ThunkApiConfig>(
  "investorRequest/submitFormThunk",
  (_, { dispatch, getState }) => {
    const isExpandedWireEnabled = FeatureToggleService.isFeatureEnabled(ToggledFeature.ExpandedWireInstructions);

    dispatch(validateFields());

    const { requestType, commonFields, customFields, validationWarningShown, draft, countries } =
      investorRequestSelector(getState());

    if (validationWarningShown) return;

    // Legacy wire instructions path: simple body, same API as other request types
    if (requestType === "update-wire-instructions" && !isExpandedWireEnabled) {
      const body: InvestorRequestBody = {
        customFields,
        requestType: requestType!,
        request: commonFields.message,
        entitledAccounts: commonFields.selectedAccounts,
        entitledEntities: commonFields.selectedFunds,
      };

      if (commonFields.attachment) {
        body.attachmentName = commonFields.attachment.name;
        (body as Record<string, any>).attachment = commonFields.attachment.base64;
      }

      dispatch(sendInvestorRequestThunk(body));

      return;
    }

    const fallbackMessage =
      requestType === "update-wire-instructions" ? "Investor Request - Update Wire Instructions" : "Investor Request";

    let message =
      commonFields.message && commonFields.message.trim().length > 0 ? commonFields.message : fallbackMessage;

    const customFieldsCopy = { ...customFields };

    if (requestType === "update-wire-instructions" && customFieldsCopy.freeText) {
      const freeText = String(customFieldsCopy.freeText).trim();

      if (freeText.length > 0) {
        message = freeText;
      }

      delete customFieldsCopy.freeText;
    }

    if (requestType === "update-wire-instructions") {
      const validCountries = countries.data.filter((c) => c && c.id && c.name);

      if (
        !customFieldsCopy.bankCountry &&
        customFieldsCopy.paymentMethod &&
        customFieldsCopy.paymentMethod !== "swift"
      ) {
        const unitedStates = validCountries.find((c) => c.name && String(c.name).toLowerCase() === "united states");

        if (unitedStates) {
          customFieldsCopy.bankCountry = unitedStates.name;
        }
      } else if (customFieldsCopy.bankCountry) {
        const country = validCountries.find(
          (c) =>
            String(c.id) === String(customFieldsCopy.bankCountry) ||
            String(c.name) === String(customFieldsCopy.bankCountry)
        );

        if (country) {
          if (String(customFieldsCopy.bankCountry) === String(country.id)) {
            customFieldsCopy.bankCountry = country.name;
          }
        }
      }

      if (customFieldsCopy.paymentMethod === "swift" && customFieldsCopy.bankCountry) {
        customFieldsCopy.beneficiaryBankCountry = customFieldsCopy.bankCountry;
      }

      if (customFieldsCopy.paymentMethod === "swift" || customFieldsCopy.paymentMethod === "fedwire") {
        if (customFieldsCopy.intermediaryYesNo === "yes") {
          const intermediaryInstructions = {
            SwiftId: customFieldsCopy.intermediarySwiftOrAba || "",
            routingNumber: customFieldsCopy.intermediaryRoutingNumber || "",
            BankName: customFieldsCopy.intermediaryBankName || "",
            PaymentMessage: customFieldsCopy.intermediaryPaymentMessage || "",
          };

          customFieldsCopy.IntermediaryInstructions = [intermediaryInstructions];
        }

        delete customFieldsCopy.intermediaryYesNo;
        delete customFieldsCopy.intermediarySwiftOrAba;
        delete customFieldsCopy.intermediaryRoutingNumber;
        delete customFieldsCopy.intermediaryBankName;
        delete customFieldsCopy.intermediaryPaymentMessage;
      }

      delete customFieldsCopy.intermediaryBankCountry;

      if (customFieldsCopy.paymentMethod === "swift") {
        delete customFieldsCopy.routingNumber;

        if (customFieldsCopy.furtherCreditYesNo === "yes") {
          customFieldsCopy.ForFurtherCredit1 = customFieldsCopy.furtherCreditLine1 || "";
          customFieldsCopy.ForFurtherCredit2 = customFieldsCopy.furtherCreditLine2 || "";
        } else {
          customFieldsCopy.ForFurtherCredit1 = "";
          customFieldsCopy.ForFurtherCredit2 = "";
        }

        delete customFieldsCopy.furtherCreditLine1;
        delete customFieldsCopy.furtherCreditLine2;
      }
    }

    const body: InvestorRequestBody = {
      customFields: customFieldsCopy,
      requestType: requestType!,
      request: message,
      entitledAccounts: commonFields.selectedAccounts,
      entitledEntities: commonFields.selectedFunds,
    };

    if (requestType === "update-wire-instructions") {
      if (draft.attachmentId && draft.attachmentName) {
        body.attachmentId = draft.attachmentId;
        body.attachmentName = draft.attachmentName;

        if (draft.attachmentURL) {
          body.attachmentURL = draft.attachmentURL;
        } else {
          const baseUrl = process.env.REACT_APP_ATTACHMENT_URL || "";

          body.attachmentURL = `${baseUrl}/ReferenceDataService/attachments/${draft.attachmentId}`;
        }
      } else if (commonFields.attachment) {
        (body as Record<string, any>).attachment = commonFields.attachment.base64;
        body.attachmentName = commonFields.attachment.name;
      }
    } else {
      if (commonFields.attachment) {
        (body as Record<string, any>).attachment = commonFields.attachment.base64;
        body.attachmentName = commonFields.attachment.name;
      }
    }

    if (requestType === "update-wire-instructions") dispatch(submitWireInstructionThunk(body));
    else dispatch(sendInvestorRequestThunk(body));
  }
);

export const fetchCountriesThunk = createAsyncThunk<Country[], void, ThunkApiConfig>(
  "investorRequest/fetchCountries",
  async (_, { rejectWithValue, extra }) => {
    try {
      const response = await extra<Array<{ id: number; countryName: string }>>(
        "entitlements/v1/user/Common/GetAllCountry",
        {
          method: "get",
        }
      );

      // Ensure the response data is an array and normalize the data structure
      const rawData = Array.isArray(response.data) ? response.data : [];

      const countries = rawData
        .filter((country: any) => country && country.id && country.countryName)
        .map((country: any) => ({
          id: String(country.id),
          name: country.countryName,
        }));

      return countries;
    } catch (error: any) {
      return rejectWithValue("Failed to load countries");
    }
  }
);

export const checkC2DSAvaliabilityThunk = createAsyncThunk<boolean, void, ThunkApiConfig>(
  "investorRequest/checkAvailability",
  async (_, { rejectWithValue, dispatch, extra }) => {
    try {
      const response = await extra<{ isAvailable: boolean; message: string }>(
        "entitlements/v1/user/WireInstruction/availability",
        {
          method: "post",
        }
      );

      if (!response.data.isAvailable) {
        const errorMessage = response.data.message;

        dispatch(
          openNotificationThunk({
            variant: "warning",
            text: errorMessage,
          })
        );
        dispatch(setWireUnavailable(true));
      } else {
        dispatch(setWireUnavailable(false));
      }

      return response.data.isAvailable;
    } catch (error: any) {
      dispatch(setWireUnavailable(true));

      dispatch(
        openNotificationThunk({
          variant: "warning",
          text: "Please try again after 5 minutes, System unavailable",
        })
      );

      return rejectWithValue(false);
    }
  }
);

// LPC-9775: Get draft values API
export const getDraftValuesThunk = createAsyncThunk<DraftValuesResponse | null, string, ThunkApiConfig>(
  "investorRequest/getDraftValues",
  async (fundType, { rejectWithValue, extra }) => {
    try {
      const response = await extra<DraftValuesResponse>(
        `/entitlements/v1/user/WireInstruction/GetDraftValues?fundType=${encodeURIComponent(fundType)}`,
        {
          method: "get",
        }
      );

      return response.data;
    } catch (error: any) {
      // Don't show error notification for 404 (no draft found)
      if (error.response?.status === 404) {
        return null;
      }

      return rejectWithValue("Failed to load draft values");
    }
  }
);

// LPC-9775: Save draft API
export const saveDraftThunk = createAsyncThunk<boolean, SaveDraftPayload, ThunkApiConfig>(
  "investorRequest/saveDraft",
  async (payload, { rejectWithValue, dispatch, extra }) => {
    try {
      const response = await extra<boolean, SaveDraftPayload>("/entitlements/v1/user/WireInstruction/PostSaveAsDraft", {
        method: "post",
        body: payload,
      });

      dispatch(
        openNotificationThunk({
          variant: "info",
          text: "Draft saved successfully.",
        })
      );

      // LPC-9775: After saving draft, fetch it back to get attachmentId for download functionality
      // This ensures the chip becomes clickable immediately after saving
      if (payload.attachmentName && payload.attachment) {
        try {
          await dispatch(getDraftValuesThunk(payload.fundType));
        } catch (error) {
          console.warn("Failed to reload draft after saving:", error);
          // Don't fail the save operation if reload fails
        }
      }

      return response.data;
    } catch (error: any) {
      const errorMessage = error.response?.data?.[0] || "Failed to save draft. Please try again.";

      dispatch(
        openNotificationThunk({
          variant: "warning",
          text: errorMessage,
        })
      );

      return rejectWithValue(errorMessage);
    }
  }
);

// LPC-9775: Download attachment from S3
export const downloadAttachmentThunk = createAsyncThunk<Blob, string, ThunkApiConfig>(
  "investorRequest/downloadAttachment",
  async (attachmentId, { rejectWithValue, dispatch, extra }) => {
    try {
      const response = await extra<Blob>(
        `/entitlements/v1/user/WireInstruction/DownloadAttachment?attachmentId=${encodeURIComponent(attachmentId)}`,
        {
          method: "get",
          responseType: "blob",
        }
      );

      return response.data;
    } catch (error: any) {
      // Handle 404 (attachment not found in S3)
      if (error.response?.status === 404) {
        dispatch(
          openNotificationThunk({
            variant: "warning",
            text: "Attachment not found. It may have been deleted.",
          })
        );

        return rejectWithValue("Attachment not found");
      }

      // Handle other errors
      dispatch(
        openNotificationThunk({
          variant: "warning",
          text: "Attachment download failed. Please try again.",
        })
      );

      return rejectWithValue("Download failed");
    }
  }
);
